import { Text, View } from "react-native";
import { Info } from "lucide-react-native";
import { ActionRowCard, CardSurface } from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { type Specimen } from "@src/packages/care/cryovault/useSpecimens";
import {
  projectFertilityPrediction,
  type ProjectionInput,
  type ProjectionStage,
} from "@src/packages/fertility-projections";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import type { PlanAction } from "../plan.types";

type Props = {
  input: ProjectionInput;
};

function observed<T>(value: T, sourceId: string) {
  return {
    value,
    provenance: {
      sourceType: "laboratory_report" as const,
      sourceId,
      verificationStatus: "unverified" as const,
    },
  };
}

function finite(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value >= 0;
}

/** Map stored semen specimens into the shared all-vault projection input. */
export function buildSemenCryosurvivalInput(
  specimens: Specimen[],
): ProjectionInput | null {
  const semen = specimens.filter((specimen) => specimen.type === "semen");
  if (semen.length === 0) return null;
  const sampleIds = semen
    .map((specimen) => specimen.sampleId)
    .filter((sampleId): sampleId is string => Boolean(sampleId));
  // A vial is not a sample. Do not build a clinical projection until the API
  // has supplied the shared sample identity for every stored vial.
  if (sampleIds.length !== semen.length) return null;
  const uniqueSampleIds = new Set(sampleIds);
  const processed = semen.filter(
    (specimen) =>
      specimen.isProcessed ||
      specimen.status === "processed" ||
      specimen.status === "stored",
  );
  return {
    kind: "frozen_semen_cohort",
    patient: {},
    semenInventory: {
      collectedSamples: observed(uniqueSampleIds.size, "specimens"),
      processedSamples: observed(processed.length, "specimens"),
      frozenVials: observed(semen.length, "specimens"),
    },
    semen: semen.map((specimen, index) => {
      const sourceId = specimen.id;
      const sampleId = sampleIds[index]!;
      const spermiogram = specimen.spermiogram;
      const thawingInfo = specimen.thawingInfo;
      return {
        sampleId,
        concentrationMillionPerMl: finite(spermiogram?.spermConcentration)
          ? observed(spermiogram.spermConcentration, sourceId)
          : undefined,
        totalMotilityPercent: finite(spermiogram?.motility)
          ? observed(spermiogram.motility, sourceId)
          : undefined,
        progressiveMotilityPercent: finite(spermiogram?.progressiveMotility)
          ? observed(spermiogram.progressiveMotility, sourceId)
          : undefined,
        morphologyPercent: finite(spermiogram?.normalMorphology)
          ? observed(spermiogram.normalMorphology, sourceId)
          : undefined,
        postThawProgressiveMotilityPercent: finite(
          thawingInfo?.motilityPostThaw,
        )
          ? observed(thawingInfo.motilityPostThaw, sourceId)
          : undefined,
        postThawViabilityPercent: finite(thawingInfo?.viability)
          ? observed(thawingInfo.viability, sourceId)
          : undefined,
        source: observed(specimen.isDonor ? "donor" : "unknown", sourceId),
      };
    }),
  };
}

export function SpermCryosurvivalEstimateWidget({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const title = t(
    "homepage.review_concept.inline.sperm_postcycle_projection_unavailable_title",
  );
  const subtitle = t(
    "homepage.review_concept.inline.sperm_postcycle_projection_unavailable_detail",
  );
  return (
    <ActionRowCard
      leading={<OrganicMiniIcon kind="sperm" size={30} />}
      title={title}
      subtitle={subtitle}
      onPress={() =>
        onAction({
          label: t("homepage.review_concept.inline.action_view_cryo_vault"),
          screen: "CryoVaultScreen",
        })
      }
      accessibilityLabel={`${title}. ${subtitle}`}
    />
  );
}

function stageFor(
  stages: ProjectionStage[],
  id: ProjectionStage["id"],
): ProjectionStage | undefined {
  return stages.find((stage) => stage.id === id);
}

function formatEstimate(stage: ProjectionStage | undefined): string {
  if (!stage) return "Not estimated";
  const { mean, low, high, unit } = stage.estimate;
  const suffix = unit === "percentage" ? "%" : "";
  const roundedMean = Math.round(mean * 10) / 10;
  if (Math.abs(high - low) < 0.1) return `${roundedMean}${suffix}`;
  return `${Math.round(low)}-${Math.round(high)}${suffix}`;
}

function countValue(stage: ProjectionStage | undefined): string {
  if (!stage) return "Not recorded";
  return `${Math.round(stage.estimate.mean)}`;
}

export function SpermCryosurvivalEstimateCard({ input }: Props) {
  const projection = projectFertilityPrediction(input, {
    now: new Date("2026-01-01T00:00:00.000Z"),
  });
  const samples = stageFor(projection.stages, "semen_samples");
  const processed = stageFor(projection.stages, "processed_semen");
  const frozenVials = stageFor(projection.stages, "frozen_vials");
  const postThawMotility =
    stageFor(projection.stages, "post_thaw_progressive_motility_percent") ??
    stageFor(projection.stages, "post_thaw_total_motility_percent") ??
    stageFor(projection.stages, "post_thaw_motility_recovery_percent");
  const postThawViability =
    stageFor(projection.stages, "post_thaw_viability_percent") ??
    stageFor(projection.stages, "post_thaw_viability_recovery_percent");
  const hasObservedPostThaw = [postThawMotility, postThawViability].some(
    (stage) => stage?.basis === "observed",
  );

  return (
    <CardSurface
      accessibilityLabel="Sperm cryosurvival estimate"
      style={{ padding: 16, gap: 14 }}
    >
      <View style={{ gap: 3 }}>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 17,
            lineHeight: 22,
            fontWeight: "900",
          }}
        >
          Sperm cryosurvival estimate
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 12,
            lineHeight: 17,
          }}
        >
          {hasObservedPostThaw
            ? "Observed post-thaw results are shown where the lab has reported them."
            : "General estimate based on the sample information currently recorded."}
        </Text>
      </View>

      <View
        style={{
          flexDirection: "row",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {[
          ["Samples", samples],
          ["Processed", processed],
          ["Frozen vials", frozenVials],
        ].map(([label, stage]) => (
          <View key={label as string} style={{ flex: 1, minWidth: 88 }}>
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 10,
                lineHeight: 13,
                fontWeight: "800",
              }}
            >
              {label as string}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.text,
                fontSize: 20,
                lineHeight: 24,
                fontWeight: "900",
              }}
            >
              {countValue(stage as ProjectionStage | undefined)}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ gap: 8 }}>
        <MetricRow
          label={
            postThawMotility?.id === "post_thaw_total_motility_percent"
              ? "Post-thaw total motility"
              : "Post-thaw progressive motility"
          }
          value={formatEstimate(postThawMotility)}
          basis={postThawMotility?.basis}
        />
        <MetricRow
          label="Post-thaw viability / recovery"
          value={formatEstimate(postThawViability)}
          basis={postThawViability?.basis}
        />
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 7,
          borderTopWidth: 1,
          borderTopColor: semanticColors.border.subtle,
          paddingTop: 10,
        }}
      >
        <Info size={14} color={EMBER.colors.textLight} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            color: EMBER.colors.textLight,
            fontSize: 11,
            lineHeight: 15,
          }}
        >
          General baseline from published sperm-bank cryosurvival cohorts. A
          clinic&apos;s own thaw results should replace it when available; this
          does not estimate fertilization, pregnancy, or live birth.
        </Text>
      </View>
    </CardSurface>
  );
}

function MetricRow({
  label,
  value,
  basis,
}: {
  label: string;
  value: string;
  basis?: ProjectionStage["basis"];
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 13,
            lineHeight: 17,
            fontWeight: "800",
          }}
        >
          {label}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 10,
            lineHeight: 13,
          }}
        >
          {basis === "observed" ? "Observed lab result" : "General estimate"}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.text,
          fontSize: 20,
          lineHeight: 24,
          fontWeight: "900",
        }}
      >
        {value}
      </Text>
    </View>
  );
}
